/**
 * Enhanced error handling utilities for the parrot wizard
 * Provides comprehensive error handling with retry mechanisms and fallback options
 */

import { fetchWithRetry, parseApiError, parseNetworkError } from './consolidatedErrorHandling.js';

/**
 * Wizard-specific error codes
 */
export const WizardErrorCodes = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  MISSING_SESSION: 'MISSING_SESSION',
  INVALID_SESSION: 'INVALID_SESSION',
  STEP_REQUIRED: 'STEP_REQUIRED',
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  VOICE_NOT_FOUND: 'VOICE_NOT_FOUND',
  OPENAI_ERROR: 'OPENAI_ERROR',
  ELEVENLABS_ERROR: 'ELEVENLABS_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  DUPLICATE_NAME: 'DUPLICATE_NAME',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  PERMISSION_DENIED: 'PERMISSION_DENIED'
};

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Wizard error class with enhanced context
 */
export class WizardError extends Error {
  constructor(message, code, step = null, field = null, severity = ErrorSeverity.MEDIUM, retryable = false) {
    super(message);
    this.name = 'WizardError';
    this.code = code;
    this.step = step;
    this.field = field;
    this.severity = severity;
    this.retryable = retryable;
    this.timestamp = new Date().toISOString();
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      step: this.step,
      field: this.field,
      severity: this.severity,
      retryable: this.retryable,
      timestamp: this.timestamp
    };
  }
}

/**
 * Parse API error response for wizard operations
 * @param {Response} response - Fetch response object
 * @param {string} step - Current wizard step
 * @returns {Promise<WizardError>} Parsed wizard error
 */
export const parseWizardApiError = async (response, step = null) => {
  try {
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();
      
      // Handle structured wizard error responses
      if (errorData.code && Object.values(WizardErrorCodes).includes(errorData.code)) {
        const severity = getSeverityForErrorCode(errorData.code);
        const retryable = isErrorRetryable(errorData.code, response.status);
        
        return new WizardError(
          errorData.error || errorData.message || 'An error occurred',
          errorData.code,
          errorData.step || step,
          errorData.field || null,
          severity,
          retryable
        );
      }
      
      // Handle validation errors with field details
      if (errorData.field_errors && Array.isArray(errorData.field_errors)) {
        const firstError = errorData.field_errors[0];
        return new WizardError(
          firstError.message,
          WizardErrorCodes.VALIDATION_ERROR,
          firstError.step || step,
          firstError.field,
          ErrorSeverity.LOW,
          false
        );
      }
      
      // Handle general API errors
      const message = errorData.error || errorData.message || 'An error occurred';
      const code = mapHttpStatusToErrorCode(response.status);
      
      return new WizardError(
        message,
        code,
        step,
        null,
        getSeverityForHttpStatus(response.status),
        isHttpStatusRetryable(response.status)
      );
    }
    
    // Handle non-JSON responses
    const textResponse = await response.text();
    const code = mapHttpStatusToErrorCode(response.status);
    
    return new WizardError(
      textResponse || `HTTP ${response.status} error`,
      code,
      step,
      null,
      getSeverityForHttpStatus(response.status),
      isHttpStatusRetryable(response.status)
    );
    
  } catch (parseError) {
    console.warn('Failed to parse wizard API error:', parseError);
    
    return new WizardError(
      `HTTP ${response.status} error`,
      mapHttpStatusToErrorCode(response.status),
      step,
      null,
      getSeverityForHttpStatus(response.status),
      isHttpStatusRetryable(response.status)
    );
  }
};
/**
 * Map HTTP status codes to wizard error codes
 * @param {number} status - HTTP status code
 * @returns {string} Wizard error code
 */
const mapHttpStatusToErrorCode = (status) => {
  switch (status) {
    case 400:
      return WizardErrorCodes.VALIDATION_ERROR;
    case 401:
    case 403:
      return WizardErrorCodes.PERMISSION_DENIED;
    case 404:
      return WizardErrorCodes.TEMPLATE_NOT_FOUND;
    case 409:
      return WizardErrorCodes.DUPLICATE_NAME;
    case 429:
      return WizardErrorCodes.QUOTA_EXCEEDED;
    case 500:
    case 502:
    case 503:
    case 504:
      return WizardErrorCodes.DATABASE_ERROR;
    default:
      return WizardErrorCodes.NETWORK_ERROR;
  }
};

/**
 * Get error severity based on error code
 * @param {string} code - Error code
 * @returns {string} Error severity
 */
const getSeverityForErrorCode = (code) => {
  switch (code) {
    case WizardErrorCodes.VALIDATION_ERROR:
    case WizardErrorCodes.DUPLICATE_NAME:
      return ErrorSeverity.LOW;
    case WizardErrorCodes.TEMPLATE_NOT_FOUND:
    case WizardErrorCodes.VOICE_NOT_FOUND:
    case WizardErrorCodes.MISSING_SESSION:
      return ErrorSeverity.MEDIUM;
    case WizardErrorCodes.OPENAI_ERROR:
    case WizardErrorCodes.ELEVENLABS_ERROR:
      return ErrorSeverity.HIGH;
    case WizardErrorCodes.DATABASE_ERROR:
    case WizardErrorCodes.PERMISSION_DENIED:
      return ErrorSeverity.CRITICAL;
    default:
      return ErrorSeverity.MEDIUM;
  }
};

/**
 * Get error severity based on HTTP status
 * @param {number} status - HTTP status code
 * @returns {string} Error severity
 */
const getSeverityForHttpStatus = (status) => {
  if (status >= 400 && status < 500) {
    return status === 401 || status === 403 ? ErrorSeverity.CRITICAL : ErrorSeverity.MEDIUM;
  }
  if (status >= 500) {
    return ErrorSeverity.HIGH;
  }
  return ErrorSeverity.LOW;
};

/**
 * Check if error code is retryable
 * @param {string} code - Error code
 * @param {number} httpStatus - HTTP status code
 * @returns {boolean} Whether error is retryable
 */
const isErrorRetryable = (code, httpStatus) => {
  const nonRetryableCodes = [
    WizardErrorCodes.VALIDATION_ERROR,
    WizardErrorCodes.DUPLICATE_NAME,
    WizardErrorCodes.PERMISSION_DENIED,
    WizardErrorCodes.TEMPLATE_NOT_FOUND,
    WizardErrorCodes.VOICE_NOT_FOUND
  ];
  
  if (nonRetryableCodes.includes(code)) {
    return false;
  }
  
  // Retry server errors and rate limiting
  return httpStatus >= 500 || httpStatus === 429;
};

/**
 * Check if HTTP status is retryable
 * @param {number} status - HTTP status code
 * @returns {boolean} Whether status is retryable
 */
const isHttpStatusRetryable = (status) => {
  return status >= 500 || status === 429;
};

/**
 * Enhanced API call with wizard-specific error handling
 * @param {Function} apiCall - API call function
 * @param {string} step - Current wizard step
 * @param {object} options - Options for retry and error handling
 * @returns {Promise} API response or throws WizardError
 */
export const callWizardApi = async (apiCall, step = null, options = {}) => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    timeout = 30000,
    fallbackData = null
  } = options;

  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await apiCall();
      
      if (!response.ok) {
        const wizardError = await parseWizardApiError(response, step);
        
        // Don't retry non-retryable errors
        if (!wizardError.retryable || attempt === maxRetries) {
          throw wizardError;
        }
        
        lastError = wizardError;
        
        // Exponential backoff with jitter
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      return await response.json();
      
    } catch (error) {
      if (error instanceof WizardError) {
        throw error;
      }
      
      // Handle network errors
      const networkError = new WizardError(
        parseNetworkError(error),
        WizardErrorCodes.NETWORK_ERROR,
        step,
        null,
        ErrorSeverity.HIGH,
        true
      );
      
      if (attempt < maxRetries) {
        lastError = networkError;
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // If we have fallback data and this is a network error, use it
      if (fallbackData && error.name === 'TypeError') {
        console.warn('Using fallback data due to network error:', error);
        return fallbackData;
      }
      
      throw networkError;
    }
  }
  
  throw lastError;
};/**
 * 
OpenAI chat error handler with retry mechanisms
 * @param {Function} chatApiCall - OpenAI chat API call function
 * @param {object} options - Options for retry and fallback
 * @returns {Promise} Chat response or fallback
 */
export const handleOpenAIChatError = async (chatApiCall, options = {}) => {
  const {
    maxRetries = 2,
    fallbackToManual = true,
    step = 'step3'
  } = options;

  try {
    return await callWizardApi(chatApiCall, step, { maxRetries });
  } catch (error) {
    if (error.code === WizardErrorCodes.OPENAI_ERROR && fallbackToManual) {
      console.warn('OpenAI chat failed, offering manual entry fallback:', error);
      
      return {
        success: false,
        error: error.message,
        fallback_available: true,
        fallback_type: 'manual_entry',
        message: 'AI chat is temporarily unavailable. You can continue with manual configuration or try again later.'
      };
    }
    
    throw error;
  }
};

/**
 * ElevenLabs synchronization error handler
 * @param {Function} syncApiCall - ElevenLabs sync API call function
 * @param {object} localConfig - Local configuration to maintain
 * @returns {Promise} Sync result with local integrity maintained
 */
export const handleElevenLabsSyncError = async (syncApiCall, localConfig) => {
  try {
    return await callWizardApi(syncApiCall, 'finalize', { maxRetries: 2 });
  } catch (error) {
    console.warn('ElevenLabs synchronization failed, maintaining local configuration:', error);
    
    // Maintain local configuration integrity even if sync fails
    return {
      success: false,
      error: error.message,
      local_config_maintained: true,
      sync_retry_available: true,
      message: 'Your parrot configuration has been saved locally. ElevenLabs synchronization will be retried automatically.',
      local_config: localConfig
    };
  }
};

/**
 * Database transaction error handler with rollback
 * @param {Function} transactionFn - Database transaction function
 * @param {object} rollbackData - Data needed for rollback
 * @returns {Promise} Transaction result
 */
export const handleDatabaseTransaction = async (transactionFn, rollbackData = {}) => {
  try {
    return await transactionFn();
  } catch (error) {
    console.error('Database transaction failed, attempting rollback:', error);
    
    // Create rollback error with context
    const rollbackError = new WizardError(
      'Database operation failed. Changes have been rolled back.',
      WizardErrorCodes.DATABASE_ERROR,
      rollbackData.step || null,
      null,
      ErrorSeverity.HIGH,
      true
    );
    
    rollbackError.rollback_data = rollbackData;
    rollbackError.original_error = error.message;
    
    throw rollbackError;
  }
};

/**
 * Quick Action template fallback handler
 * @param {number} templateId - Template ID that failed
 * @param {object} formData - Current form data
 * @returns {object} Fallback template configuration
 */
export const handleTemplateFallback = (templateId, formData) => {
  console.warn(`Template ${templateId} not found, using fallback configuration`);
  
  // Create a basic fallback template based on form data
  const fallbackTemplate = {
    id: 'fallback',
    name: 'Basic Assistant',
    description: 'A helpful AI assistant for general inquiries',
    system_prompt: `You are ${formData.name || 'a helpful AI assistant'}. Answer calls professionally and assist callers with their inquiries. Be polite, helpful, and direct callers to appropriate resources when needed.`,
    example_first_message: formData.first_message || 'Hello! How can I help you today?',
    is_fallback: true
  };
  
  return {
    template: fallbackTemplate,
    is_fallback: true,
    original_template_id: templateId,
    message: 'The selected template is temporarily unavailable. Using a basic configuration instead.'
  };
};

/**
 * Format wizard error for user display
 * @param {WizardError|Error} error - Error to format
 * @param {string} context - Additional context
 * @returns {object} Formatted error for UI display
 */
export const formatWizardErrorForDisplay = (error, context = '') => {
  if (!(error instanceof WizardError)) {
    // Convert regular errors to wizard errors
    error = new WizardError(
      error.message || 'An unexpected error occurred',
      WizardErrorCodes.NETWORK_ERROR,
      null,
      null,
      ErrorSeverity.MEDIUM,
      true
    );
  }
  
  const userFriendlyMessages = {
    [WizardErrorCodes.VALIDATION_ERROR]: 'Please check your input and try again.',
    [WizardErrorCodes.MISSING_SESSION]: 'Your session has expired. Please start over.',
    [WizardErrorCodes.INVALID_SESSION]: 'Your session is invalid. Please start over.',
    [WizardErrorCodes.TEMPLATE_NOT_FOUND]: 'The selected template is no longer available.',
    [WizardErrorCodes.VOICE_NOT_FOUND]: 'The selected voice is no longer available.',
    [WizardErrorCodes.OPENAI_ERROR]: 'AI chat is temporarily unavailable.',
    [WizardErrorCodes.ELEVENLABS_ERROR]: 'Voice service is temporarily unavailable.',
    [WizardErrorCodes.DUPLICATE_NAME]: 'A parrot with this name already exists.',
    [WizardErrorCodes.QUOTA_EXCEEDED]: 'You have reached your usage limit.',
    [WizardErrorCodes.PERMISSION_DENIED]: 'You do not have permission to perform this action.',
    [WizardErrorCodes.NETWORK_ERROR]: 'Network connection error. Please check your internet connection.',
    [WizardErrorCodes.DATABASE_ERROR]: 'A server error occurred. Please try again later.'
  };
  
  return {
    message: error.message,
    userMessage: userFriendlyMessages[error.code] || error.message,
    code: error.code,
    step: error.step,
    field: error.field,
    severity: error.severity,
    retryable: error.retryable,
    timestamp: error.timestamp,
    context,
    canRetry: error.retryable && error.severity !== ErrorSeverity.CRITICAL,
    showDetails: error.severity === ErrorSeverity.HIGH || error.severity === ErrorSeverity.CRITICAL
  };
};
